(() => {
  if (customElements.get('lb-pdp-photos')) return;
  const clamp = (value, low, high) => Math.max(low, Math.min(high, value));

  class ProductPhotos extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.addEventListener('click', event => {
        const source = event.target.closest('[data-lb-photo]');
        if (!source || event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
        if (event.detail && this.railDragged) {
          event.preventDefault();
          return;
        }
        const dialog = this.querySelector('dialog[data-lb-viewer]');
        if (!dialog || !dialog.showModal) return;
        event.preventDefault();
        if (this.dialog !== dialog) this.bindViewer(dialog);
        this.openPhoto(source);
      }, options);
      // Touch Events keep both contacts together on iOS, even if the gesture
      // starts on the rail and continues after the modal has opened.
      let pinch = null;
      const measure = touches => {
        const [a, b] = touches;
        return {distance: Math.max(1, Math.hypot(a.clientX-b.clientX, a.clientY-b.clientY)),
          x: (a.clientX+b.clientX)/2, y: (a.clientY+b.clientY)/2};
      };
      const touchOptions = {signal: this.events.signal, capture:true, passive:false};
      this.addEventListener('touchstart', e => {
        if (e.touches.length !== 2) return;
        const first = e.touches[0].target;
        const frame = first.closest?.('[data-lb-photo]');
        const dialog = this.querySelector('[data-lb-viewer]');
        if (!dialog || (!frame && !first.closest?.('[data-lb-stage]'))) return;
        e.preventDefault();
        this.touchPinching = true;
        if (!dialog.open) {
          if (this.dialog !== dialog) this.bindViewer(dialog);
          this.openPhoto(frame);
        }
        pinch = measure(e.touches);
      }, touchOptions);
      this.addEventListener('touchmove', e => {
        if (!this.touchPinching) return;
        e.preventDefault();
        if (!pinch || e.touches.length !== 2) return;
        const next = measure(e.touches);
        this.pinchBy(next.distance / pinch.distance, pinch, next);
        pinch = next;
      }, touchOptions);
      const finishPinch = e => {
        if (!this.touchPinching) return;
        if (e.cancelable) e.preventDefault();
        pinch = null;
        if (!e.touches.length) this.touchPinching = false;
      };
      this.addEventListener('touchend', finishPinch, touchOptions);
      this.addEventListener('touchcancel', finishPinch, touchOptions);
      // The touchscreen and trackpad scroll the rail natively. A mouse can also drag it.
      this.addEventListener('pointerdown', event => {
        const rail = event.target.closest('.lb-pdp-rail');
        this.railDragged = false;
        if (rail && rail.scrollWidth > rail.clientWidth + 1 && event.pointerType === 'mouse' && event.button === 0) {
          this.drag = { rail, x: event.clientX, left: rail.scrollLeft, id: event.pointerId };
        }
      }, options);
      this.addEventListener('pointermove', event => {
        const drag = this.drag;
        if (!drag || event.pointerId !== drag.id) return;
        const delta = event.clientX - drag.x;
        if (!this.railDragged && Math.abs(delta) < 6) return;
        if (!this.railDragged) drag.rail.setPointerCapture(event.pointerId);
        this.railDragged = true;
        drag.rail.scrollLeft = drag.left - delta;
      }, options);
      for (const eventName of ['pointerup', 'pointercancel', 'lostpointercapture']) {
        this.addEventListener(eventName, () => { this.drag = null; }, options);
      }
      this.addEventListener('pointerleave', () => {
        if (!this.railDragged) this.drag = null;
      }, options);
      this.addEventListener('dragstart', event => {
        if (this.drag) event.preventDefault();
      }, options);
    }

    disconnectedCallback() {
      this.events.abort();
      this.viewerEvents?.abort();
      this.resizeObserver?.disconnect();
      if (this.dialog?.open) this.dialog.close();
      this.dialog = null;
      this.drag = null;
    }

    bindViewer(dialog) {
      this.viewerEvents?.abort();
      this.resizeObserver?.disconnect();
      this.viewerEvents = new AbortController();
      const signal = this.viewerEvents.signal;
      this.dialog = dialog;
      const get = name => dialog.querySelector('[data-lb-' + name + ']');
      const stage = get('stage'), image = get('zoom-image');
      const minus = get('minus'), plus = get('plus'), prev = get('prev'), next = get('next');
      const scaleLabel = get('scale'), count = get('count'), close = get('close');
      const pointers = new Map();
      let active, width = 1, height = 1, fit = 1, zoom = 1, left = 0, top = 0;
      let start = null, moved = false;
      const photos = () => Array.from(this.querySelectorAll('.lb-pdp-rail [data-lb-photo]'))
        .filter(link => link.getClientRects().length);
      const offset = (position, extent, viewport) =>
        extent <= viewport ? (viewport - extent) / 2 : clamp(position, viewport - extent, 0);
      const maxZoom = () => window.matchMedia('(max-width: 989px)').matches ? 4 : 2.5;
      function paint() {
        const scale = fit * zoom;
        left = offset(left, width * scale, stage.clientWidth);
        top = offset(top, height * scale, stage.clientHeight);
        image.style.transform = 'translate(' + left + 'px,' + top + 'px) scale(' + scale + ')';
        scaleLabel.textContent = Math.round(zoom * 100) + '%';
        minus.disabled = zoom <= 1;
        plus.disabled = zoom >= maxZoom();
        stage.classList.toggle('is-magnified', zoom > 1);
        stage.classList.toggle('is-dragging', pointers.size > 0 && zoom > 1);
      }
      function initialFit() {
        const horizontal = stage.clientWidth / width;
        const vertical = stage.clientHeight / height;
        const contain = Math.min(horizontal, vertical);
        if (width >= height || !window.matchMedia('(max-width: 989px) and (orientation: portrait)').matches) return contain;
        return Math.min(Math.max(horizontal, vertical), contain * 1.25);
      }
      function reset() {
        if (!dialog.open || !stage.clientWidth || !stage.clientHeight) return;
        fit = initialFit();
        zoom = 1;
        left = (stage.clientWidth - width * fit) / 2;
        top = (stage.clientHeight - height * fit) / 2;
        image.style.width = width + 'px';
        image.style.height = height + 'px';
        paint();
      }
      function select(source) {
        if (!source) return;
        active = source;
        const thumb = source.querySelector('img');
        const frames = photos(), index = frames.indexOf(source);
        width = Number(source.dataset.width) || thumb.naturalWidth || thumb.width || 1;
        height = Number(source.dataset.height) || thumb.naturalHeight || thumb.height || 1;
        image.alt = thumb.alt;
        image.src = source.dataset.full || thumb.currentSrc || thumb.src;
        count.textContent = (index + 1) + ' / ' + frames.length;
        prev.disabled = index <= 0;
        next.disabled = index >= frames.length - 1;
        pointers.clear();
        start = null;
        reset();
      }
      function navigate(step) {
        const frames = photos();
        select(frames[clamp(frames.indexOf(active) + step, 0, frames.length - 1)]);
      }
      function setZoom(value, from, to = from) {
        const target = clamp(value, 1, maxZoom()), ratio = target / zoom;
        left = to.x - (from.x - left) * ratio;
        top = to.y - (from.y - top) * ratio;
        zoom = target;
        paint();
      }
      function point(event) {
        const box = stage.getBoundingClientRect();
        return { x: event.clientX - box.left, y: event.clientY - box.top };
      }
      this.pinchBy = (ratio, before, after) => {
        pointers.clear(); start = null;
        const r = stage.getBoundingClientRect();
        setZoom(zoom * ratio, {x:before.x-r.left, y:before.y-r.top}, {x:after.x-r.left, y:after.y-r.top});
      };
      const center = () => ({ x: stage.clientWidth / 2, y: stage.clientHeight / 2 });
      function pair() {
        const [a, b] = pointers.values();
        return { center: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
          distance: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)) };
      }

      this.openPhoto = source => {
        // Refresh the photo list on every open and navigation: variants may replace or reorder it.
        if (!dialog.open) dialog.showModal();
        select(source);
        close.focus({ preventScroll: true });
      };
      close.addEventListener('click', () => dialog.close(), { signal });
      dialog.addEventListener('close', () => {
        pointers.clear();
        if (!active?.isConnected) return;
        const rail = active.closest('.lb-pdp-rail');
        if (rail && rail.scrollWidth > rail.clientWidth + 1) {
          const frame = active.getBoundingClientRect(), bounds = rail.getBoundingClientRect();
          if (frame.left < bounds.left || frame.right > bounds.right) rail.scrollLeft += frame.left - bounds.left;
        }
        active.focus({ preventScroll: true });
      }, { signal });
      prev.addEventListener('click', () => navigate(-1), { signal });
      next.addEventListener('click', () => navigate(1), { signal });
      minus.addEventListener('click', () => setZoom(1, center()), { signal });
      plus.addEventListener('click', () => setZoom(maxZoom(), center()), { signal });
      image.addEventListener('error', () => {
        const thumb = active?.querySelector('img');
        const fallback = thumb?.currentSrc || thumb?.src;
        if (!fallback || image.src === fallback) return;
        width = thumb.naturalWidth || width;
        height = thumb.naturalHeight || height;
        image.src = fallback;
        reset();
      }, { signal });

      stage.addEventListener('wheel', event => {
        if (!event.deltaY || event.metaKey) return;
        event.preventDefault();
        const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? stage.clientHeight : 1;
        setZoom(zoom * Math.exp(-clamp(event.deltaY * unit, -360, 360) * .004), point(event));
      }, { signal, passive: false });
      stage.addEventListener('pointerdown', event => {
        if (this.touchPinching || event.button !== 0 || pointers.size >= 2) return;
        const at = point(event);
        pointers.set(event.pointerId, at);
        start = pointers.size === 1 ? at : null;
        moved = false;
        stage.setPointerCapture(event.pointerId);
        paint();
      }, { signal });
      stage.addEventListener('pointermove', event => {
        const before = pointers.get(event.pointerId);
        if (this.touchPinching || !before) return;
        const at = point(event);
        const oldPair = pointers.size === 2 ? pair() : null;
        pointers.set(event.pointerId, at);
        if (oldPair) {
          const newPair = pair();
          setZoom(zoom * newPair.distance / oldPair.distance, oldPair.center, newPair.center);
        } else {
          if (start && Math.hypot(at.x - start.x, at.y - start.y) > 6) moved = true;
          if (zoom > 1) {
            left += at.x - before.x;
            top += at.y - before.y;
            paint();
          }
        }
      }, { signal });
      function end(event) {
        if (!pointers.delete(event.pointerId) || this.touchPinching) return;
        if (event.type === 'pointerup' && start && !pointers.size) {
          const at = point(event), dx = at.x - start.x, dy = at.y - start.y;
          if (!moved && Math.hypot(dx, dy) <= 6) setZoom(zoom >= maxZoom() ? 1 : maxZoom(), at);
          else if (zoom === 1 && event.pointerType !== 'mouse' && Math.abs(dx) > 48 && Math.abs(dx) > 1.4 * Math.abs(dy)) navigate(dx < 0 ? 1 : -1);
        }
        start = null;
        paint();
      }
      for (const eventName of ['pointerup', 'pointercancel', 'lostpointercapture']) {
        stage.addEventListener(eventName, end, { signal });
      }
      dialog.addEventListener('keydown', event => {
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        switch (event.key) {
          case 'ArrowLeft': navigate(-1); break;
          case 'ArrowRight': navigate(1); break;
          case '+': case '=': setZoom(maxZoom(), center()); break;
          case '-': case '0': setZoom(1, center()); break;
          default: return; // Escape retains the native dialog behavior.
        }
        event.preventDefault();
      }, { signal });
      if (window.ResizeObserver) {
        let lastWidth = 0, lastHeight = 0;
        this.resizeObserver = new ResizeObserver(() => {
          if (!dialog.open) return;
          if (stage.clientWidth !== lastWidth || stage.clientHeight !== lastHeight) {
            lastWidth = stage.clientWidth;
            lastHeight = stage.clientHeight;
            fit = initialFit();
            paint();
          }
        });
        this.resizeObserver.observe(stage);
      }
    }
  }
  customElements.define('lb-pdp-photos', ProductPhotos);

  // Quick view reuses this section's HTML. Only replace the gallery on the actual product page.
  customElements.define('lb-pdp-photo-mount', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || !this.closest('main .product-section') ||
          this.closest('quick-add-modal,quick-view-modal,#quick-view,.quick-add-modal,.qv-wrapper')) return;
        const column = this.parentElement;
        const legacy = this.nextElementSibling;
        const template = this.querySelector('template');
        if (!column?.matches('.product__media-wrapper[data-section]') ||
          column.dataset.section.includes('quickadd') || legacy?.tagName !== 'DIV' ||
          !legacy.querySelector('[data-media-id]') || !template?.content.querySelector('lb-pdp-photos')) return;
        const replacement = template.content.cloneNode(true);
        for (const nav of legacy.querySelectorAll('nav.breadcrumb')) replacement.append(nav);
        for (const slider of legacy.querySelectorAll('.swiper')) {
          if (slider.swiper && !slider.swiper.destroyed) slider.swiper.destroy();
        }
        legacy.replaceWith(replacement);
        this.remove();
      });
    }
  });
})();
